import { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Camera, 
  Keyboard, 
  X, 
  CheckCircle, 
  AlertTriangle,
  ArrowLeft,
  Loader2,
  Upload,
  Video,
  VideoOff,
  SwitchCamera,
  RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import SignatureCanvas from 'react-signature-canvas';
import { scanBarcode, confirmDelivery, confirmCollection, createDamageReport } from '../lib/api';
import { toast } from 'sonner';

const StatusBadge = ({ status }) => {
  const styles = {
    available: 'badge-success',
    on_hire: 'badge-info',
    damaged: 'badge-error',
    maintenance: 'badge-warning',
  };
  return (
    <span className={`${styles[status] || 'badge-neutral'} inline-block`}>
      {status.replace('_', ' ')}
    </span>
  );
};

export default function ScannerPage() {
  const { action } = useParams();
  const navigate = useNavigate();
  const [mode, setMode] = useState('scan'); // scan, manual, result
  const [manualInput, setManualInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [scannedData, setScannedData] = useState(null);
  const [condition, setCondition] = useState('ok');
  const [damageDescription, setDamageDescription] = useState('');
  const [damagePhoto, setDamagePhoto] = useState(null);
  const [recipientName, setRecipientName] = useState('');
  const [assetPhoto, setAssetPhoto] = useState(null);  // Photo of asset for delivery/collection
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [scannerReady, setScannerReady] = useState(false);
  const [currentCameraIndex, setCurrentCameraIndex] = useState(0);
  const [availableCameras, setAvailableCameras] = useState([]);
  const signatureRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);

  const actionTitle = {
    delivery: 'Scan for Delivery',
    collection: 'Scan for Collection',
    damage: 'Report Damage',
    find: 'Find Asset',
  }[action] || 'Scanner';

  const stopCamera = useCallback(() => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
    setScannerReady(false);
  }, []);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  const startCamera = async (preferredDeviceId = null) => {
    try {
      setCameraError(null);
      
      // First, enumerate all video devices
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(d => d.kind === 'videoinput');
      setAvailableCameras(videoDevices);
      
      let stream;
      
      if (preferredDeviceId) {
        // Use specific device ID if provided (for camera switching)
        stream = await navigator.mediaDevices.getUserMedia({
          video: { 
            deviceId: { exact: preferredDeviceId },
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: false
        });
      } else {
        // Try to get rear camera - multiple strategies
        
        // Strategy 1: Find camera with 'back' or 'rear' in label
        const rearCamera = videoDevices.find(d => 
          d.label.toLowerCase().includes('back') || 
          d.label.toLowerCase().includes('rear') ||
          d.label.toLowerCase().includes('environment') ||
          d.label.toLowerCase().includes('0, facing back')
        );
        
        if (rearCamera) {
          console.log('Found rear camera by label:', rearCamera.label);
          stream = await navigator.mediaDevices.getUserMedia({
            video: { 
              deviceId: { exact: rearCamera.deviceId },
              width: { ideal: 1280 },
              height: { ideal: 720 }
            },
            audio: false
          });
          // Update camera index
          const idx = videoDevices.findIndex(d => d.deviceId === rearCamera.deviceId);
          if (idx >= 0) setCurrentCameraIndex(idx);
        } else {
          // Strategy 2: Try facingMode constraint
          try {
            stream = await navigator.mediaDevices.getUserMedia({
              video: { 
                facingMode: { exact: 'environment' },
                width: { ideal: 1280 },
                height: { ideal: 720 }
              },
              audio: false
            });
            console.log('Got camera with facingMode: environment');
          } catch (e) {
            // Strategy 3: Use last camera in list (usually rear on mobile)
            console.log('FacingMode failed, using last camera in list');
            const lastCamera = videoDevices[videoDevices.length - 1];
            if (lastCamera) {
              stream = await navigator.mediaDevices.getUserMedia({
                video: { 
                  deviceId: { exact: lastCamera.deviceId },
                  width: { ideal: 1280 },
                  height: { ideal: 720 }
                },
                audio: false
              });
              setCurrentCameraIndex(videoDevices.length - 1);
            } else {
              throw new Error('No camera available');
            }
          }
        }
      }
      
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          setCameraActive(true);
          setScannerReady(true);
          startBarcodeDetection();
        };
      }
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError(err.message || 'Failed to access camera. Please use manual entry.');
      setCameraActive(false);
    }
  };

  const switchCamera = async () => {
    if (availableCameras.length <= 1) return;
    
    // Stop current camera
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
    }
    
    // Switch to next camera
    const nextIndex = (currentCameraIndex + 1) % availableCameras.length;
    setCurrentCameraIndex(nextIndex);
    
    // Start with new camera
    await startCamera(availableCameras[nextIndex].deviceId);
  };

  const startBarcodeDetection = () => {
    // Use BarcodeDetector API if available (Chrome, Edge)
    if ('BarcodeDetector' in window) {
      const barcodeDetector = new window.BarcodeDetector({
        formats: ['code_128', 'code_39', 'ean_13', 'ean_8', 'upc_a', 'upc_e', 'qr_code']
      });

      scanIntervalRef.current = setInterval(async () => {
        if (videoRef.current && videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA) {
          try {
            const barcodes = await barcodeDetector.detect(videoRef.current);
            if (barcodes.length > 0) {
              const barcode = barcodes[0].rawValue;
              stopCamera();
              toast.success(`Barcode scanned: ${barcode}`);
              handleBarcodeSearch(barcode);
            }
          } catch (err) {
            // Ignore detection errors
          }
        }
      }, 200);
    } else {
      // Fallback message for browsers without BarcodeDetector
      setCameraError('Barcode detection not supported in this browser. Please use manual entry or try Chrome/Edge.');
      stopCamera();
    }
  };

  const handleBarcodeSearch = async (barcode) => {
    setLoading(true);
    try {
      const response = await scanBarcode(barcode.trim().toUpperCase());
      setScannedData(response.data);
      setMode('result');
    } catch (error) {
      toast.error('Asset not found');
    } finally {
      setLoading(false);
    }
  };

  const handleManualSearch = async () => {
    if (!manualInput.trim()) return;
    await handleBarcodeSearch(manualInput);
  };

  const handlePhotoUpload = (e, type = 'damage') => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'asset') {
          setAssetPhoto(reader.result);
        } else {
          setDamagePhoto(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  };

  const handleConfirmAction = async () => {
    if (!scannedData?.asset) return;
    
    const signatureData = signatureRef.current?.isEmpty() 
      ? '' 
      : signatureRef.current?.toDataURL();

    if (!signatureData && action !== 'find' && action !== 'damage') {
      toast.error('Please provide a signature');
      return;
    }

    if (!recipientName.trim() && (action === 'delivery' || action === 'collection')) {
      toast.error('Please enter the recipient name');
      return;
    }

    setLoading(true);
    try {
      if (action === 'delivery') {
        await confirmDelivery({
          asset_id: scannedData.asset.asset_id,
          hire_order_id: scannedData.hire_order?.order_number || '',
          signature_base64: signatureData,
          recipient_name: recipientName.trim(),
          delivery_photo: assetPhoto || null,
        });
        toast.success('Delivery confirmed!');
      } else if (action === 'collection') {
        await confirmCollection({
          asset_id: scannedData.asset.asset_id,
          hire_order_id: scannedData.hire_order?.order_number || '',
          condition,
          damage_description: condition === 'damaged' ? damageDescription : null,
          damage_photo: condition === 'damaged' ? damagePhoto : null,
          signature_base64: signatureData,
          recipient_name: recipientName.trim(),
          collection_photo: assetPhoto || null,
        });
        toast.success('Collection confirmed!');
      } else if (action === 'damage') {
        await createDamageReport({
          asset_id: scannedData.asset.asset_id,
          hire_order_id: scannedData.hire_order?.order_number || '',
          description: damageDescription,
          photo_base64: damagePhoto,
          signature_base64: signatureData,
        });
        toast.success('Damage reported!');
      }
      
      navigate('/mobile');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  const renderScanMode = () => (
    <div className="space-y-4">
      {/* Camera Scanner */}
      <Card className="border-2 border-slate-200 shadow-industrial overflow-hidden">
        <CardContent className="p-0">
          <div className="relative bg-black" style={{ minHeight: '300px' }}>
            {/* Video element for camera feed */}
            <video
              ref={videoRef}
              className={`w-full h-auto ${cameraActive ? 'block' : 'hidden'}`}
              playsInline
              muted
              style={{ maxHeight: '400px', objectFit: 'cover' }}
            />
            <canvas ref={canvasRef} className="hidden" />
            
            {/* Scanning overlay */}
            {cameraActive && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-64 h-32 border-4 border-primary rounded-lg relative">
                  <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-primary -mt-1 -ml-1"></div>
                  <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-primary -mt-1 -mr-1"></div>
                  <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-primary -mb-1 -ml-1"></div>
                  <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-primary -mb-1 -mr-1"></div>
                  {/* Scan line animation */}
                  <div className="absolute left-2 right-2 h-0.5 bg-primary/70 animate-pulse" style={{ top: '50%' }}></div>
                </div>
              </div>
            )}

            {/* Start camera prompt */}
            {!cameraActive && !cameraError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-4">
                <Camera className="w-16 h-16 mb-4 text-slate-400" />
                <p className="text-slate-400 mb-4 text-center">Camera not started</p>
                <Button 
                  onClick={() => startCamera()}
                  className="bg-primary hover:bg-primary-hover"
                  data-testid="start-camera-btn"
                >
                  <Video className="w-4 h-4 mr-2" />
                  Start Rear Camera
                </Button>
              </div>
            )}
            
            {/* Error state */}
            {cameraError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-4">
                <VideoOff className="w-12 h-12 mb-4 text-red-400" />
                <p className="text-red-400 text-center text-sm mb-4">{cameraError}</p>
                <Button 
                  onClick={() => { setCameraError(null); setMode('manual'); }}
                  variant="outline"
                  className="border-white text-white hover:bg-white/10"
                >
                  Use Manual Entry
                </Button>
              </div>
            )}
          </div>
          
          {/* Camera controls */}
          {cameraActive && (
            <div className="p-3 bg-slate-900">
              <p className="text-white text-sm font-body mb-3 text-center">Point camera at barcode</p>
              <div className="flex justify-center gap-3">
                {availableCameras.length > 1 && (
                  <Button 
                    onClick={switchCamera}
                    variant="outline"
                    size="sm"
                    className="border-white/30 text-white hover:bg-white/10"
                    data-testid="switch-camera-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Switch Camera
                  </Button>
                )}
                <Button 
                  onClick={stopCamera}
                  variant="ghost"
                  size="sm"
                  className="text-slate-400 hover:text-white"
                >
                  <VideoOff className="w-4 h-4 mr-1" />
                  Stop
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual Entry Option */}
      <div className="text-center">
        <button
          onClick={() => { stopCamera(); setMode('manual'); }}
          className="inline-flex items-center gap-2 text-primary hover:underline font-body"
          data-testid="manual-entry-btn"
        >
          <Keyboard className="w-4 h-4" />
          Enter Asset ID Manually
        </button>
      </div>
    </div>
  );

  const renderManualMode = () => (
    <Card className="border-2 border-slate-200 shadow-industrial">
      <CardHeader className="border-b border-slate-100">
        <CardTitle className="font-heading font-bold uppercase text-sm">
          Enter Asset ID
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        <div>
          <Label className="font-heading font-bold uppercase text-xs">Asset ID or Barcode</Label>
          <Input
            value={manualInput}
            onChange={(e) => setManualInput(e.target.value.toUpperCase())}
            placeholder="AST-XXXXXXXX"
            className="mt-1 h-12 border-2 border-slate-200 font-mono text-lg"
            data-testid="manual-input"
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && handleManualSearch()}
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setMode('scan')}
            className="flex-1 h-12 border-2 border-slate-200"
          >
            <Camera className="w-4 h-4 mr-2" />
            Back to Scanner
          </Button>
          <Button
            onClick={handleManualSearch}
            disabled={loading || !manualInput.trim()}
            className="flex-1 h-12 bg-primary hover:bg-primary-hover"
            data-testid="search-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderResultMode = () => {
    if (!scannedData?.asset) return null;
    const { asset, hire_order } = scannedData;

    return (
      <div className="space-y-4">
        {/* Asset Info */}
        <Card className="border-2 border-slate-200 shadow-industrial">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <img
                src={asset.photo_url || 'https://images.unsplash.com/photo-1632095710940-ad578e8cbe6b?w=100'}
                alt={asset.name}
                className="w-20 h-20 rounded object-cover border-2 border-slate-200"
              />
              <div className="flex-1">
                <h2 className="font-heading font-bold text-xl">{asset.name}</h2>
                <p className="font-mono text-sm text-slate-500">{asset.asset_id}</p>
                <StatusBadge status={asset.status} />
              </div>
            </div>
            {hire_order && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <p className="font-mono text-xs uppercase text-slate-400 mb-1">Hire Order</p>
                <p className="font-body font-semibold">{hire_order.order_number}</p>
                <p className="font-body text-sm text-slate-600">{hire_order.client_name}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Condition Check for Collection */}
        {action === 'collection' && (
          <Card className="border-2 border-slate-200 shadow-industrial">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="font-heading font-bold uppercase text-sm">
                Condition Check
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setCondition('ok')}
                  className={`p-4 border-2 rounded flex flex-col items-center gap-2 transition-colors ${
                    condition === 'ok' 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-slate-200 hover:border-green-300'
                  }`}
                  data-testid="condition-ok-btn"
                >
                  <CheckCircle className={`w-8 h-8 ${condition === 'ok' ? 'text-green-500' : 'text-slate-400'}`} />
                  <span className="font-heading font-bold uppercase text-sm">OK</span>
                </button>
                <button
                  onClick={() => setCondition('damaged')}
                  className={`p-4 border-2 rounded flex flex-col items-center gap-2 transition-colors ${
                    condition === 'damaged' 
                      ? 'border-red-500 bg-red-50' 
                      : 'border-slate-200 hover:border-red-300'
                  }`}
                  data-testid="condition-damaged-btn"
                >
                  <AlertTriangle className={`w-8 h-8 ${condition === 'damaged' ? 'text-red-500' : 'text-slate-400'}`} />
                  <span className="font-heading font-bold uppercase text-sm">Damaged</span>
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Damage Report Fields */}
        {(condition === 'damaged' || action === 'damage') && (
          <Card className="border-2 border-slate-200 shadow-industrial">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="font-heading font-bold uppercase text-sm">
                Damage Details
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div>
                <Label className="font-heading font-bold uppercase text-xs">Description</Label>
                <Textarea
                  value={damageDescription}
                  onChange={(e) => setDamageDescription(e.target.value)}
                  placeholder="Describe the damage..."
                  className="mt-1 border-2 border-slate-200"
                  rows={3}
                  data-testid="damage-description"
                />
              </div>
              <div>
                <Label className="font-heading font-bold uppercase text-xs">Photo</Label>
                <div className="mt-1">
                  {damagePhoto ? (
                    <div className="relative">
                      <img 
                        src={damagePhoto} 
                        alt="Damage" 
                        className="w-full h-40 object-cover rounded border-2 border-slate-200"
                      />
                      <button
                        onClick={() => setDamagePhoto(null)}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-slate-300 rounded cursor-pointer hover:border-primary">
                      <Upload className="w-8 h-8 text-slate-400 mb-2" />
                      <span className="font-body text-sm text-slate-500">Take or upload photo</span>
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={(e) => handlePhotoUpload(e, 'damage')}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recipient Name - for delivery and collection */}
        {(action === 'delivery' || action === 'collection') && (
          <Card className="border-2 border-slate-200 shadow-industrial">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="font-heading font-bold uppercase text-sm">
                Recipient Details
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div>
                <Label className="font-heading font-bold uppercase text-xs">
                  Recipient Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  placeholder="Enter name of person receiving/returning"
                  className="mt-1 h-12 border-2 border-slate-200"
                  data-testid="recipient-name-input"
                />
                <p className="font-mono text-xs text-slate-400 mt-1">
                  Name of the person {action === 'delivery' ? 'receiving the delivery' : 'returning the equipment'}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Asset Photo - for delivery and collection */}
        {(action === 'delivery' || action === 'collection') && (
          <Card className="border-2 border-slate-200 shadow-industrial">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="font-heading font-bold uppercase text-sm">
                Asset Photo
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div>
                <Label className="font-heading font-bold uppercase text-xs">
                  Take a photo of the asset
                </Label>
                <div className="mt-2">
                  {assetPhoto ? (
                    <div className="relative">
                      <img 
                        src={assetPhoto} 
                        alt="Asset" 
                        className="w-full h-48 object-cover rounded border-2 border-slate-200"
                      />
                      <button
                        onClick={() => setAssetPhoto(null)}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-slate-300 rounded cursor-pointer hover:border-primary transition-colors">
                      <Camera className="w-8 h-8 text-slate-400 mb-2" />
                      <span className="font-body text-sm text-slate-500">
                        {action === 'delivery' ? 'Photo at delivery' : 'Photo at collection'}
                      </span>
                      <span className="font-mono text-xs text-slate-400 mt-1">Tap to capture</span>
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={(e) => handlePhotoUpload(e, 'asset')}
                        className="hidden"
                        data-testid="asset-photo-input"
                      />
                    </label>
                  )}
                </div>
                <p className="font-mono text-xs text-slate-400 mt-2">
                  Optional: Document the asset condition at {action === 'delivery' ? 'delivery' : 'collection'}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Signature */}
        {action !== 'find' && (
          <Card className="border-2 border-slate-200 shadow-industrial">
            <CardHeader className="border-b border-slate-100 flex flex-row items-center justify-between">
              <CardTitle className="font-heading font-bold uppercase text-sm">
                Signature
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={clearSignature}>
                Clear
              </Button>
            </CardHeader>
            <CardContent className="p-4">
              <div className="border-2 border-slate-300 rounded bg-white">
                <SignatureCanvas
                  ref={signatureRef}
                  canvasProps={{
                    className: 'signature-pad w-full h-40',
                    'data-testid': 'signature-pad'
                  }}
                />
              </div>
              <p className="font-mono text-xs text-slate-400 mt-2 text-center">
                Sign above to confirm
              </p>
            </CardContent>
          </Card>
        )}

        {/* Action Button */}
        <Button
          onClick={handleConfirmAction}
          disabled={loading}
          className="w-full h-14 bg-primary hover:bg-primary-hover font-heading font-bold uppercase tracking-wide text-lg shadow-industrial-sm"
          data-testid="confirm-action-btn"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : action === 'find' ? (
            'Done'
          ) : action === 'damage' ? (
            'Submit Damage Report'
          ) : (
            `Confirm ${action === 'delivery' ? 'Delivery' : 'Collection'}`
          )}
        </Button>
      </div>
    );
  };

  return (
    <div className="max-w-lg mx-auto space-y-4" data-testid="scanner-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button 
          variant="outline" 
          size="icon" 
          onClick={() => { stopCamera(); mode === 'result' ? setMode('manual') : navigate('/mobile'); }}
          className="border-2 border-slate-200"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-heading font-black text-xl uppercase tracking-tight">
          {actionTitle}
        </h1>
      </div>

      {/* Content */}
      {mode === 'scan' && renderScanMode()}
      {mode === 'manual' && renderManualMode()}
      {mode === 'result' && renderResultMode()}
    </div>
  );
}
